import {
  Alert,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { Dialog } from '@/components/AppDialog'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { useState } from 'react'
import { format, parseISO } from 'date-fns'
import { useDeleteMuhurtham, useSaveMuhurtham, useTamilDates } from '@/api/hooks'
import type { MuhurthamDate } from '@/api/types'
import { ErrorBanner, isoDate } from '@/components/common'

/**
 * The muhurtham dates the business will take bookings on.
 *
 * <p>They are not computed and never could be - they are published yearly in
 * the almanac and differ by tradition and by family. So they are typed in off
 * the page, one at a time, and the calendar marks them.</p>
 *
 * <p>The date is the whole of it. Everything else a panchangam prints beside
 * one - the star, the auspicious hours, which almanac it came from - was asked
 * for and left blank, because what the office needs off the page is which days
 * are worth holding a hall for.</p>
 */
export function MuhurthamDialog(props: {
  open: boolean
  onClose: () => void
  existing: MuhurthamDate[]
  monthLabel: string
}) {
  const remove = useDeleteMuhurtham()

  return (
    <Dialog open={props.open} onClose={props.onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Muhurtham dates</DialogTitle>
      <DialogContent>
        <ErrorBanner error={remove.error} />

        <ManualEntry />

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" gutterBottom>
          Already held for {props.monthLabel}
        </Typography>
        {props.existing.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No muhurtham dates for this month yet.
          </Typography>
        ) : (
          <MuhurthamTable
            dates={props.existing}
            onRemove={(id) => remove.mutate(id)}
            removing={remove.isPending}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

/**
 * One muhurtham date, typed in from the almanac.
 *
 * <p>The Tamil date is shown back as the day is picked, because that is how the
 * almanac prints it and how the customer will ask for it - seeing "Thai 5" come
 * up confirms the right day was typed. A date landing on a new or full moon is
 * called out, since no wedding muhurtham falls on one.</p>
 */
function ManualEntry() {
  // Opens on today. A date field that opens empty is a date somebody types
  // out in full before they can do the thing they came to do.
  const [date, setDate] = useState(() => isoDate(0))
  const [saved, setSaved] = useState<string | null>(null)

  const save = useSaveMuhurtham()
  // One day's reading, to show the Tamil date back as it is typed.
  const { data: tamil } = useTamilDates(date || '2000-01-01', date || '2000-01-01')
  const reading = date ? tamil?.[0] : undefined

  const submit = () =>
    save.mutate(
      // A muhurtham list is a wedding list; the other occasions were on the
      // form because the field exists, not because anybody chose between them.
      { date, occasionType: 'WEDDING' },
      {
        onSuccess: (entry) => {
          setSaved(`${entry.date} saved under ${entry.tamilMonth ?? 'its Tamil month'}`)
          setDate('')
        },
      },
    )

  return (
    <>
      <ErrorBanner error={save.error} />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Take one date off the almanac. The Tamil month is worked out from the date.
      </Typography>

      <TextField
        required
        type="date"
        label="Date"
        value={date}
        onChange={(event) => setDate(event.target.value)}
        InputLabelProps={{ shrink: true }}
        sx={{ maxWidth: 240 }}
      />

      {reading ? (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
          <Chip
            size="small"
            variant="outlined"
            label={`${reading.monthInTamil} ${reading.day} - ${reading.label}`}
          />
          <Typography variant="caption" color="text.secondary">
            {reading.yearName} year
          </Typography>
        </Stack>
      ) : null}

      {reading?.moonDay ? (
        <Alert severity="warning" sx={{ mt: 1 }}>
          This day is {reading.moonDayLabel}. No wedding muhurtham falls on a{' '}
          {reading.moonDay === 'AMAVASAI' ? 'new' : 'full'} moon - check the date before saving.
        </Alert>
      ) : null}

      {saved ? (
        <Alert severity="success" sx={{ mt: 1 }} onClose={() => setSaved(null)}>
          {saved}
        </Alert>
      ) : null}

      <Button
        variant="contained"
        sx={{ mt: 2, display: 'block' }}
        onClick={submit}
        disabled={!date || save.isPending}
      >
        Save this date
      </Button>
    </>
  )
}

/**
 * The dates held for the month.
 *
 * <p>Two columns, because two is all there is: the day, and the Tamil month it
 * falls in. The star, the hours and the almanac were columns while a pasted
 * list could fill them; nothing fills them now, and a column of dashes says
 * less than no column at all.</p>
 */
function MuhurthamTable(props: {
  dates: MuhurthamDate[]
  onRemove?: (id: string) => void
  removing?: boolean
}) {
  return (
    <Table size="small" sx={{ mt: 1 }}>
      <TableHead>
        <TableRow>
          <TableCell>Date</TableCell>
          <TableCell>Tamil month</TableCell>
          {props.onRemove ? <TableCell /> : null}
        </TableRow>
      </TableHead>
      <TableBody>
        {props.dates.map((entry) => (
          <TableRow key={entry.id} hover>
            <TableCell>{format(parseISO(entry.date), 'EEE dd-MM-yyyy')}</TableCell>
            <TableCell>{entry.tamilMonth ?? '—'}</TableCell>
            {props.onRemove ? (
              <TableCell align="right">
                <IconButton
                  size="small"
                  disabled={props.removing}
                  onClick={() => props.onRemove?.(entry.id)}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </TableCell>
            ) : null}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
